import {
    Button,
    ButtonGroup,
    ColorPicker,
    Dialog,
    Flexer,
    Heading,
    Icon,
    If,
    Input,
    InputControl,
    InputPrefix,
    Modal,
    ModalClose,
    Pill,
    Popover,
    Portal,
    Stack,
    Text,
    View,
    useDialog,
    useInput,
    useVisibility,
} from '@fold-dev/core'
import { useEffect, useMemo, useState } from 'react'
import { TbPencil, TbTag, TbTrash, TbX } from 'react-icons/tb'
import { useNavigate, useParams } from 'react-router'
import { v4 as uuidv4 } from 'uuid'
import { Error, Label, Loading } from '../components'
import { byText, logger } from '../helpers/util'
import { useLabels, useWorkspaces } from '../hooks/api.hooks'
import { useAppStore } from '../store/app'

const LabelRow = ({ label, onSave, onDelete }) => {
    const [color, setColor] = useState(label.color)
    const text = useInput(label.text)
    const description = useInput(label.description)
    const dialog = useVisibility(false)
    const { show, delayedShow, hide, visible } = useVisibility(false)

    const handleSave = () => {
        dialog.hide()
        onSave({
            id: label.id,
            color,
            text: text.value,
            description: description.value,
        })
    }

    return (
        <>
            <Modal
                width={500}
                portal={Portal}
                isVisible={dialog.visible}
                onDismiss={dialog.hide}>
                <Stack
                    noStretch
                    spacing={10}
                    height={35}>
                    <InputControl flex={1}>
                        <InputPrefix>
                            <Popover
                                zIndex={100}
                                width={250}
                                anchor="bottom-left"
                                isVisible={visible}
                                onDismiss={hide}
                                content={
                                    <ColorPicker
                                        p={10}
                                        width={250}
                                        onChange={setColor}
                                        color={color}
                                    />
                                }>
                                <View
                                    radius={50}
                                    width={15}
                                    height={15}
                                    bg={color}
                                    className="f-buttonize"
                                    onClick={show}
                                />
                            </Popover>
                        </InputPrefix>
                        <Input
                            placeholder="Enter label text"
                            value={text.value}
                            onChange={text.onChange}
                        />
                    </InputControl>
                    <Input
                        display="none"
                        flex={2}
                        placeholder="Enter label description"
                        value={description.value}
                        onChange={description.onChange}
                    />
                    <Button
                        width={75}
                        onClick={dialog.hide}>
                        Cancel
                    </Button>
                    <Button onClick={onDelete}>
                        <Icon icon={TbTrash} />
                    </Button>
                    <Button
                        width={75}
                        variant="accent"
                        onClick={handleSave}>
                        Save
                    </Button>
                </Stack>
            </Modal>

            <Label
                onClick={dialog.show}
                id={label.id}
                color={label.color}
                text={label.text}
            />
        </>
    )
}

const Labels = (props) => {
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(null)
    const { workspaceId, projectId } = useParams()
    const { getAll, create, remove, update } = useLabels()
    const [labels, setLabels] = useState([])
    const { show, delayedShow, hide, visible } = useVisibility(false)
    const [color, setColor] = useState('#491CFF')
    const text = useInput('')
    const description = useInput('')
    const [labelDeleteId, setLabelDeleteId] = useState(null)
    const actions = useAppStore((state) => state.actions)
    const workspace = useAppStore((state) => state.workspace)
    //const isAdmin = useMemo(() => workspace.role == 'Owner' || workspace.role == 'Admin', [workspace.role])
    const isAdmin = useMemo(() => workspace.role != 'Guest', [workspace.role])

    const handleCreate = async () => {
        setError(null)

        try {
            const newLabel = {
                id: uuidv4(),
                color,
                text: text.value,
                description: description.value,
            }

            await create(workspaceId, newLabel)

            text.setValue('')
            description.setValue('')
            actions.labelsAdd(newLabel)
            setLabels([...labels, newLabel].sort(byText))
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleSave = async ({ id, color, text, description }) => {
        setError(null)

        try {
            actions.labelsUpdate({ id, color, text })
            actions.taskUpdateUpdateLabel({ label: { id, color, text } })
            await update(workspaceId, id, { color, text, description })
            fetchData()
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleDelete = async () => {
        setError(null)

        try {
            actions.labelsDelete(labelDeleteId)
            actions.taskUpdateRemoveLabel({ labelId: labelDeleteId })
            setLabelDeleteId(null)
            setLabels(labels.filter((l) => l.id != labelDeleteId))
            await remove(workspaceId, labelDeleteId)
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const fetchData = async () => {
        setError(null)

        try {
            const labels = await getAll(workspaceId)
            setLabels(labels.sort(byText))
        } catch (e) {
            logger(e)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    useEffect(() => {
        fetchData()
    }, [])

    return (
        <>
            <Error error={error} />
            <Loading loading={loading} />

            <Stack
                display="block"
                direction="vertical"
                spacing={15}
                width="100%"
                height="100%">
                <View>
                    <Stack
                        noStretch
                        spacing={10}
                        height={35}>
                        <InputControl flex={1}>
                            <InputPrefix>
                                <Popover
                                    zIndex={100}
                                    width={250}
                                    anchor="bottom-left"
                                    isVisible={visible}
                                    onDismiss={hide}
                                    content={
                                        <ColorPicker
                                            p={10}
                                            width={250}
                                            onChange={setColor}
                                            color={color}
                                        />
                                    }>
                                    <View
                                        radius={50}
                                        width={15}
                                        height={15}
                                        bg={color}
                                        className="f-buttonize"
                                        onClick={show}
                                    />
                                </Popover>
                            </InputPrefix>
                            <Input
                                placeholder="Enter label text"
                                value={text.value}
                                onChange={text.onChange}
                            />
                        </InputControl>
                        {/* <Input
                            flex={1.5}
                            placeholder="Enter label description"
                            value={description.value}
                            onChange={description.onChange}
                        /> */}
                        <Button
                            disabled={!text.value || !isAdmin}
                            width={75}
                            variant="accent"
                            onClick={handleCreate}>
                            Create
                        </Button>
                    </Stack>
                </View>

                <View
                    row
                    gap="0.5rem"
                    justifyContent="flex-start"
                    wrap="wrap"
                    style={
                        {
                            '--f-common-label-height': '28px',
                            '--f-common-label-font-size': '1rem',
                            '--f-common-label-max-width': 'fit-content',
                        } as any
                    }>
                    {labels.sort(byText).map((label, index) => (
                        <LabelRow
                            key={label.id}
                            label={label}
                            onSave={handleSave}
                            onDelete={() => setLabelDeleteId(label.id)}
                        />
                    ))}
                </View>

                <If if={labels.length == 0}>
                    <View
                        height="100%"
                        colorToken="text-weak"
                        column
                        p="0 0 5rem 0">
                        <Icon
                            icon={TbTag}
                            size="xl"
                        />
                        <Text
                            colorToken="text-weak"
                            p="1rem">
                            You don't have any labels yet.
                        </Text>
                    </View>
                </If>

                <br />

                <Dialog
                    portal={Portal}
                    closeButton
                    isVisible={!!labelDeleteId}
                    title="Delete this label?"
                    description="This action can not be undone."
                    onDismiss={() => setLabelDeleteId(null)}
                    footer={
                        <>
                            <Button onClick={() => setLabelDeleteId(null)}>Cancel</Button>
                            <Flexer />
                            <Button
                                onClick={handleDelete}
                                variant="danger"
                                outline>
                                Delete
                            </Button>
                        </>
                    }
                />
            </Stack>
        </>
    )
}

export const LabelsModal = ({ onDismiss }) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>(null)
    const [selected, setSelected] = useState(0)
    const { workspaceId, projectId } = useParams()
    const { setDialog, closeDialog } = useDialog()
    const workspacesAPI = useWorkspaces()
    const navigate = useNavigate()

    return (
        <Modal
            disableBackgroundDismiss
            width={600}
            height="80%"
            anchor="middle-center"
            onDismiss={onDismiss}
            isVisible={true}
            style={{ '--f-modal-body-padding': 0, 'maxHeight': 900 }}
            header={
                <>
                    <Heading as="h4">Labels</Heading>
                    <Flexer />
                    <ModalClose onClick={onDismiss} />
                </>
            }>
            <View
                p="var(--f-tabs-panel-padding)"
                position="absolute"
                style={{ inset: 0 }}
                className="f-overflow-y-auto">
                <Labels />
            </View>
        </Modal>
    )
}
